import { useCustomerStore } from '@stores/me/customer'
import { useDisciplineStore } from '@stores/discipline'

import { useKidRoutes } from '@composables/routes/kid-route.hook'
import { useDiscipline, useRawDiscipline } from '@hooks/discipline/index.hook'

import { isCompletedBooking } from '@utils/booking'
import { pick } from '@helpers/object'
import amplitudeConfig from '@config/amplitude.json'

import { trackEvent } from '@tools/amplitude'

interface BtnPlansPayload {
  query?: Record<string, string>
  text?: string | null
}

interface BtnPlansReturn {
  text: string
  selected: ComputedRef<boolean>
  disabled: ComputedRef<boolean>
  plansUrl: ComputedRef<string | null>
  setRedirect: () => Promise<void>
}

export const useBtnPlans = (payload: BtnPlansPayload): BtnPlansReturn => {
  const customerStore = useCustomerStore()
  const disciplineStore = useDisciplineStore()

  const {
    isDisciplineEla,
  } = useDiscipline({ discipline: disciplineStore.getDisciplineCode })

  const {
    padMath,
    padEla,
    pricingMath,
    pricingEla,
    isRoutePad,
    isRoutePricing,
  } = useKidRoutes()

  const text = payload.text || 'Check prices'

  const selected = computed(() => isRoutePad.value || isRoutePricing.value)
  const disabled = computed(() => !customerStore.isSomeDisciplineDemoCompleted)

  const plansMath = computed(() => customerStore.isTypePaid ? pricingMath.value : padMath.value)
  const plansEla = computed(() => customerStore.isTypePaid ? pricingEla.value : padEla.value)

  const plansUrl = computed(() => {
    if (customerStore.isAllDisciplinesDemoCompleted) {
      return isDisciplineEla.value
        ? plansEla.value
        : plansMath.value
    } else {
      const kid = customerStore.getKidInCustomer
      const disciplineWithCompletedDemo = kid?.disciplines.find(discipline => isCompletedBooking(discipline?.demo || null)) || null
      if (!disciplineWithCompletedDemo) {
        return null
      } else {
        const ela = useRawDiscipline({ discipline: disciplineWithCompletedDemo.code }).isDisciplineEla
        return ela
          ? plansEla.value
          : plansMath.value
      }
    }
  })

  const setRedirect = async (): Promise<void> => {
    trackEvent(amplitudeConfig.events.clickButton, pick({
      notify_type: payload.query?.notify_type,
      from: payload.query?.from,
      button_name: 'button_plans',
      button_text: payload.text,
    }))

    if (plansUrl.value) {
      await navigateTo({
        path: plansUrl.value,
        query: payload.query || {},
      })
    }
  }

  return {
    text,
    selected,
    disabled,
    plansUrl,
    setRedirect,
  }
}
